import React, { useEffect, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { IconAdd, IconCheck, IconDisconnect, IconMinus } from '../../../../shared/icon/icon'
import { SlackMark } from '../../../../shared/icon/slack-mark'
import { formatAgo, formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useAuditRecord } from '../../../../shared/audit/history/audit-record-context'
import { useAlertRoutingAccess } from './access'
import { getConnection, listTargets } from './alert-routing-api'
import { connectionState, missingScopesOf, providerLabel } from './connection-field'
import { abilitiesOf, unknownMissingOf, useConnectionActions } from './connection-actions'
import { RUNS_PAGE, connectionHref, newTargetHref, runsForTarget, targetHref } from './paths'
import { connectionSetup, connectionSetupPill } from './setup-words'
import { whereOf } from './target-words'
import {
  AlertPlainPage, AlertWorkspace, ArSection, EmptyLine, LinkRows, SetupNotice, SetupPill, SubHead, TypeLogo, When, eventItem, runItem,
  useRecentRuns, useRecordEvents
} from './workspace-parts'

export { disconnectText } from './connection-actions'

export const CONNECTION_SECTIONS = [
  { id: 'permissions', label: 'Permissions' },
  { id: 'targets', label: 'Targets' },
  { id: 'activity', label: 'Activity' }
]

// A Slack target posts through this workspace (its config names it; auth's
// target view carries the connection).
export const postsThrough = (target, connectionID) =>
  String((target.connection && target.connection.id) || (target.config || {}).connection_id || '') === String(connectionID)

// "Slack workspace · T03B0DZA1 · bot DEV EducationXR"
export const connectionSubtitle = (c, label) =>
  [`${label} workspace`, c.externalID, c.botName && `bot ${c.botName}`, c.scopeType !== 'platform' && c.organizationID && `Organization ${c.organizationID}`]
    .filter(Boolean).join(' · ')

const STATUS_DOT = { connected: 'ok', needs_approval: 'warn', needs_reconnect: 'bad', disconnected: 'warn' }

// The status tile's line under its pill.
const statusDetail = (c, state) => {
  if (state.key === 'connected') return 'every permission approved'
  if (state.key === 'needs_approval') return `${plural(missingScopesOf(c).length, 'new permission')} to approve`
  if (state.key === 'disconnected') return c.disconnectedAt ? `since ${formatAgo(c.disconnectedAt)}` : 'alerts are held'
  return c.statusReason || 'alerts are held'
}

// The summary strip (status-strip.js tiles): each leads to its section.
export const connectionTiles = (c, targets, setup) => {
  const state = connectionState(c)
  const list = targets || c.targets || []
  const channels = list.map(t => whereOf(t)).filter(Boolean)
  const usedBy = c.usedBy || 0
  return [
    { key: 'status', label: 'status', value: state.label, pill: true, tone: state.tone, detail: statusDetail(c, state), dot: STATUS_DOT[state.key] || null, section: 'permissions' },
    {
      key: 'targets', label: 'targets', value: String(list.length), tone: setup ? 'warn' : null, dot: setup ? 'warn' : list.length ? 'ok' : null, section: 'targets',
      detail: list.length ? (channels.length > 1 ? `${channels[0]} and ${channels.length - 1} more` : channels[0] || plural(list.length, 'target')) : 'none yet'
    },
    { key: 'routes', label: 'routes', value: String(usedBy), detail: usedBy ? `send through ${list.length === 1 ? 'its target' : 'its targets'}` : 'none send here yet', section: 'targets' },
    {
      key: 'posted', label: 'last alert posted', value: c.lastPostedAt ? formatAgo(c.lastPostedAt) : 'Never', section: 'activity',
      detail: c.lastPostedAt ? formatDateTime(c.lastPostedAt) : 'nothing has reached it yet'
    },
    {
      key: 'by', label: 'connected by', value: (c.connectedBy && c.connectedBy.name) || 'Unknown', section: 'activity',
      detail: c.connectedAt ? formatAgo(c.connectedAt) : ''
    }
  ]
}

// /admin/alert-routing/connections/:connectionID (the address the "Slack needs
// reconnecting" alert links to): one connected workspace as a page, in the SSO
// connection workspace's anatomy (workspace-parts.js). The header: the Slack
// mark, the workspace's name, "Slack workspace · <id> · bot <name>", its status
// pill (leading to Permissions), Reconnect as the primary action and Disconnect
// last in the ⋮, in red, asking first (connection-actions.js, the same actions
// as its row). The strip: status, targets, routes, the last alert posted, who
// connected it. Then Permissions (what EducationXR can do, in words), Targets
// (each opening its page, with + New Slack target: the new-target form with
// this workspace chosen) and Activity (the alerts delivered through its
// targets, each opening its detail, and its own audit entries, each opening the
// entry). History is the connection's (channel_connection).
export const ConnectionWorkspace = () => {
  const { connectionID } = useParams()
  return <ConnectionWorkspaceBody key={connectionID} connectionID={connectionID} />
}

const ConnectionWorkspaceBody = ({ connectionID }) => {
  const history = useHistory()
  const location = useLocation()
  const { canEdit, reason } = useAlertRoutingAccess()
  const [arrived] = useState(() => location.state || {})
  const [connection, setConnection] = useState(null)
  const [targets, setTargets] = useState(undefined) // their full views; null: couldn't be read
  const [error, failure, setError] = useErrorLine()
  const [said, setSaid] = useState('')
  const actions = useConnectionActions({
    onDisconnected: (answer, gone) => {
      setConnection(c => ({ ...c, ...(answer || {}), status: (answer && answer.status) || 'disconnected' }))
      setSaid(`${gone.externalName} is disconnected.`)
    }
  })

  useEffect(() => {
    getConnection(connectionID).then(setConnection, err => setError(serverMessage(err, "This connection couldn't be loaded."), err))
    listTargets().then(all => setTargets((all || []).filter(t => postsThrough(t, connectionID))), () => setTargets(null))
  }, [connectionID, setError])

  // A flash from the consent page is shown once: the address keeps none of it.
  useEffect(() => {
    if (location.state && location.state.flash) history.replace({ pathname: location.pathname, search: location.search })
  }, [location, history])

  // History: whose connection this is, once loaded (null: a platform one).
  const loaded = Boolean(connection)
  useAuditRecord({ type: 'channel_connection', id: loaded ? connectionID : undefined, organizationID: loaded ? (connection.organizationID ?? null) : undefined })

  const shown = targets || (connection && connection.targets) || undefined
  const runs = useRecentRuns(shown ? shown.map(t => t.id) : undefined)
  const { events, sys } = useRecordEvents('channel_connection', connectionID, connection && connection.status)

  if (!connection) {
    return (
      <AlertPlainPage list="connections" crumb="Connection" title={error ? 'Connection not found' : 'Loading…'} logo={<TypeLogo Icon={SlackMark} />}>
        {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      </AlertPlainPage>
    )
  }

  const label = providerLabel(connection.provider)
  const state = connectionState(connection)
  const setup = connectionSetup({ ...connection, targets: shown || connection.targets })
  const missing = missingScopesOf(connection)
  const abilities = abilitiesOf(connection)
  const waitingOthers = state.key === 'needs_approval' ? unknownMissingOf(connection).length : 0
  const usable = connection.status === 'connected'
  const leaving = actions.consent.busy
  const reconnect = () => actions.reconnect(connection)
  const newTarget = `${newTargetHref('slack_app')}&connection=${encodeURIComponent(connection.id)}`
  const toNewTarget = () => history.push(newTarget)

  const headerActions = canEdit &&
    <>
      <button type="button" className="btn sw-btn-primary" disabled={leaving} onClick={reconnect}
        title={state.key === 'needs_approval' ? 'Approve the new permissions' : `Approve EducationXR in ${label} again`}>Reconnect</button>
      {connection.status !== 'disconnected' &&
        <Menu sentenceCase forceDropdown>
          <MenuItem Icon={IconDisconnect} text="Disconnect" danger pin={false} show onClick={() => actions.askDisconnect(connection)} />
        </Menu>}
    </>

  const reasonNotice = state.key === 'needs_reconnect'
    ? { tone: 'bad', text: connection.statusReason || `EducationXR can't post to this workspace. Its targets are held until it is reconnected.` }
    : state.key === 'disconnected'
      ? { tone: 'warn', text: 'Disconnected: its targets are held. Reconnect to post again.' }
      : null

  return (
    <AlertWorkspace list="connections" crumb={connection.externalName}
      title={connection.externalName} subtitle={connectionSubtitle(connection, label)} logo={<TypeLogo Icon={SlackMark} />}
      status={{ label: state.label, tone: state.tone, section: 'permissions', reason: statusDetail(connection, state) }}
      badges={connectionSetupPill(connection, state.key) ? <SetupPill /> : null}
      headerActions={headerActions}
      tiles={connectionTiles(connection, shown, setup)} sections={CONNECTION_SECTIONS}>
      <div className="ar-notices">
        {arrived.flash && <Notice tone="ok" role="status">{arrived.flash}</Notice>}
        {!canEdit && reason && <Notice tone="warn">{reason}</Notice>}
        {said && <Notice tone="ok" role="status">{said}</Notice>}
        {actions.consent.error && <Notice tone="bad" role="alert">{actions.consent.error}<ErrorReference error={actions.consent.failure} compact /></Notice>}
        {reasonNotice &&
          <div className={`sw-notice ${reasonNotice.tone} ar-setup`} role="status">
            <div className="ar-setup-row">
              <span>{reasonNotice.text}</span>
              {canEdit && <button type="button" className="btn sw-btn btn-sm" disabled={leaving} onClick={reconnect}>Reconnect</button>}
            </div>
          </div>}
        {setup && <SetupNotice setup={setup} onAction={canEdit && usable ? toNewTarget : null} />}
      </div>

      <ArSection id="permissions" title="Permissions" lede={`What EducationXR can do in this ${label} workspace.`}>
        {state.key === 'needs_approval' &&
          <div className="sw-notice warn ar-setup mb-3" role="status">
            <div className="ar-setup-row">
              <span>{`${plural(missing.length, 'new permission')} to approve. Alerts keep posting meanwhile.`}</span>
              {canEdit && <button type="button" className="btn sw-btn btn-sm" disabled={leaving} onClick={reconnect}>Approve in {label}</button>}
            </div>
          </div>}
        <ul className="ar-abilities">
          {abilities.map(a =>
            <li key={a.key} className={a.allowed ? undefined : 'is-off'} title={`${label} permissions: ${a.codes}`}>
              <span className="ar-ability-icon" aria-hidden="true">{a.allowed ? <IconCheck /> : <IconMinus />}</span>
              <span className="ar-ability-text">{a.label}</span>
              {!a.allowed && <span className="sw-hint">{a.waiting ? 'Once approved' : a.key === 'create' ? 'Asked for when you first create one' : 'Not allowed'}</span>}
            </li>)}
          {waitingOthers > 0 && !abilities.some(a => a.key === 'other') &&
            <li className="is-off" title={`${label} permissions: ${unknownMissingOf(connection).join(', ')}`}>
              <span className="ar-ability-icon" aria-hidden="true"><IconMinus /></span>
              <span className="ar-ability-text">{`Other ${label} permissions (${waitingOthers})`}</span>
              <span className="sw-hint">Once approved</span>
            </li>}
        </ul>
      </ArSection>

      <ArSection id="targets" title="Targets" lede="The channels alerts post to through this workspace."
        actions={canEdit &&
          <button type="button" className="btn sw-btn btn-sm" disabled={!usable} title={usable ? undefined : 'Reconnect the workspace first.'} onClick={toNewTarget}>
            <IconAdd /> New Slack target
          </button>}>
        {shown === undefined && <EmptyLine>Loading…</EmptyLine>}
        {shown && shown.length === 0 && <EmptyLine>No target posts through this workspace yet.</EmptyLine>}
        {shown && shown.length > 0 &&
          <LinkRows label="Targets" items={shown.map(t => ({
            key: t.id,
            to: targetHref(t.id),
            title: t.name,
            sub: [whereOf(t), t.usedBy !== undefined && plural(t.usedBy, 'route')].filter(Boolean).join(' · '),
            meta: targets && (t.usedBy || 0) === 0 ? <SetupPill /> : null,
            when: targets ? (t.lastSuccessAt ? <>Delivered <When at={t.lastSuccessAt} /></> : 'Never delivered') : undefined
          }))} />}
      </ArSection>

      <ArSection id="activity" title="Activity" lede="Alerts delivered through its targets, and changes to the connection.">
        <SubHead title="Alerts delivered"
          aside={shown && shown.length > 0 && <Link className="small" to={shown.length === 1 ? runsForTarget(shown[0].id) : RUNS_PAGE}>View all in Alert runs</Link>} />
        {runs === undefined && <EmptyLine>Loading…</EmptyLine>}
        {runs === null && <EmptyLine>The alerts couldn't be loaded.</EmptyLine>}
        {runs && runs.length === 0 && <EmptyLine>No alert has gone through its targets yet.</EmptyLine>}
        {runs && runs.length > 0 && <LinkRows label="Alerts delivered" items={runs.map(r => runItem(r, connectionHref(connection.id)))} />}

        {events !== null &&
          <div className="mt-4">
            <SubHead title="Connection events"
              aside={<Link className="small" to={`/admin/audit-log?rec=${encodeURIComponent(`channel_connection:${connection.id}`)}`}>Open in Audit log</Link>} />
            {events === undefined && <EmptyLine>Loading…</EmptyLine>}
            {events && events.length === 0 && <EmptyLine>Nothing recorded yet.</EmptyLine>}
            {events && events.length > 0 && <LinkRows label="Connection events" items={events.map(e => eventItem(e, sys))} />}
          </div>}
      </ArSection>
      {actions.dialogs}
    </AlertWorkspace>
  )
}
